import { useEffect, useRef } from "react";

/**
 * useWakeLock — keeps the device screen awake while `active` is true.
 *
 * Uses the standard Screen Wake Lock API (navigator.wakeLock). Supported on
 * Chrome/Edge/Android WebView (including our Google Play TWA) and modern
 * Safari. Silently no-ops on browsers that don't support it.
 *
 * Behavior:
 *  - Requests a `screen` wake lock when `active` becomes true.
 *  - Releases it when `active` becomes false or the component unmounts.
 *  - Automatically re-acquires the lock when the tab becomes visible again
 *    (browsers release wake locks on `visibilitychange` → hidden, per spec).
 *  - First acquire attempt may fail without a user gesture; a listener on
 *    `pointerdown`/`keydown` retries once the user interacts.
 */
export default function useWakeLock(active) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;

    let cancelled = false;

    const acquire = async () => {
      if (cancelled || sentinelRef.current) return;
      if (document.visibilityState !== "visible") return;
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        // Clear our ref if the browser releases the lock for any reason
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } catch {
        // NotAllowedError before any user gesture — the retry listener below
        // handles that case. Other errors we simply ignore.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };
    const onInteract = () => acquire();

    acquire();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pointerdown", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) s.release().catch(() => {});
    };
  }, [active]);
}
